import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormHelperText,
  alpha,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Edit } from '@mui/icons-material';
import { User } from '../api/admin';
import { Client } from '../types/admin';
import { updateUser, fetchClients } from '../api/admin';
import { formatApiError } from '../utils/helpers';

const THEME = {
  BG_PRIMARY: "#000000",
  BG_SECONDARY: "#1a1a1a",
  BG_TERTIARY: "#2d2d2d",
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  TEXT_MUTED: "#808080",
  ACCENT_PRIMARY: "#2196f3",
  ACCENT_SUCCESS: "#4caf50",
  ACCENT_WARNING: "#ff9800",
  ACCENT_ERROR: "#ff4444",
  BORDER_SUBTLE: "#333333",
  BORDER_DEFAULT: "#404040",
};

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onSuccess: () => void;
  isSuperAdmin: boolean;
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  user,
  onSuccess,
  isSuperAdmin
}) => {
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    full_name: user.full_name || '',
    password: '',
    role: user.role || 'user',
    client_id: user.client_id || 0,
    language: user.language || 'IT'
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);

  useEffect(() => {
    if (open) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name || '',
        password: '',
        role: user.role || 'user',
        client_id: user.client_id || 0,
        language: user.language || 'IT'
      });
      setError(null);
      setSuccess(false);
      loadClientsData();
    }
  }, [open, user]);

  const loadClientsData = async () => {
    if (!isSuperAdmin) {
      setLoadingClients(false);
      return;
    }
    
    try {
      const data = await fetchClients();
      setClients(data);
    } catch (err) {
      // Error('Errore nel caricamento dei clienti:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepara i dati da inviare (non inviare password vuota)
      const updateData: any = {
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        language: formData.language
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      if (isSuperAdmin && formData.client_id) {
        updateData.client_id = formData.client_id;
      }

      await updateUser(user.id, updateData);
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail 
        ? formatApiError(err.response.data.detail)
        : 'Impossibile aggiornare l\'utente';
      setError(errorMessage);
      // Error('Errore nell\'aggiornamento dell\'utente:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: THEME.BG_SECONDARY,
          border: `1px solid ${THEME.BORDER_SUBTLE}`,
          borderRadius: 2,
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: THEME.TEXT_PRIMARY, 
        borderBottom: `1px solid ${THEME.BORDER_SUBTLE}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Edit sx={{ color: THEME.ACCENT_WARNING }} />
        <Box>
          <Typography variant="h6" component="span">
            Modifica Utente
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: THEME.TEXT_MUTED, mt: 0.5 }}>
            {user.username}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, bgcolor: alpha(THEME.ACCENT_ERROR, 0.1) }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.1) }}>
              Utente aggiornato con successo!
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: THEME.SURFACE_GLASS,
                  color: THEME.TEXT_PRIMARY,
                  '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
                },
                '& .MuiInputLabel-root': { color: THEME.TEXT_SECONDARY },
                '& .MuiInputLabel-root.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }}
            />

            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: THEME.SURFACE_GLASS,
                  color: THEME.TEXT_PRIMARY,
                  '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
                },
                '& .MuiInputLabel-root': { color: THEME.TEXT_SECONDARY },
                '& .MuiInputLabel-root.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }}
            />

            <TextField
              label="Nome Completo"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: THEME.SURFACE_GLASS,
                  color: THEME.TEXT_PRIMARY,
                  '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
                },
                '& .MuiInputLabel-root': { color: THEME.TEXT_SECONDARY },
                '& .MuiInputLabel-root.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }}
            />

            <TextField
              label="Nuova Password (lascia vuoto per mantenere)"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              placeholder="Lascia vuoto per mantenere la password attuale"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: THEME.TEXT_MUTED }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: THEME.SURFACE_GLASS,
                  color: THEME.TEXT_PRIMARY,
                  '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
                },
                '& .MuiInputLabel-root': { color: THEME.TEXT_SECONDARY },
                '& .MuiInputLabel-root.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ color: THEME.TEXT_SECONDARY }}>Ruolo</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Ruolo"
                disabled={!isSuperAdmin && formData.role === 'super_admin'}
                sx={{
                  bgcolor: THEME.SURFACE_GLASS,
                  color: THEME.TEXT_PRIMARY,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_SUBTLE },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DEFAULT },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_PRIMARY },
                  '& .MuiSvgIcon-root': { color: THEME.TEXT_SECONDARY }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: THEME.BG_SECONDARY,
                      border: `1px solid ${THEME.BORDER_SUBTLE}`,
                      '& .MuiMenuItem-root': {
                        color: THEME.TEXT_PRIMARY,
                        '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1) },
                        '&.Mui-selected': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2) }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="user">Utente</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                {isSuperAdmin && <MenuItem value="super_admin">Super Admin</MenuItem>}
              </Select>
              {!isSuperAdmin && formData.role === 'super_admin' && (
                <FormHelperText sx={{ color: THEME.TEXT_MUTED }}>
                  Solo super_admin può assegnare il ruolo super_admin
                </FormHelperText>
              )}
            </FormControl>

            {isSuperAdmin && (
              <FormControl fullWidth>
                <InputLabel sx={{ color: THEME.TEXT_SECONDARY }}>Cliente</InputLabel>
                <Select
                  value={formData.client_id || ''}
                  onChange={(e) => setFormData({ ...formData, client_id: Number(e.target.value) })}
                  label="Cliente"
                  disabled={loadingClients}
                  sx={{
                    bgcolor: THEME.SURFACE_GLASS,
                    color: THEME.TEXT_PRIMARY,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_SUBTLE },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DEFAULT },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_PRIMARY },
                    '& .MuiSvgIcon-root': { color: THEME.TEXT_SECONDARY }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: THEME.BG_SECONDARY,
                        border: `1px solid ${THEME.BORDER_SUBTLE}`,
                        '& .MuiMenuItem-root': {
                          color: THEME.TEXT_PRIMARY,
                          '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1) },
                          '&.Mui-selected': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2) }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">-- Seleziona Cliente --</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${THEME.BORDER_SUBTLE}`, gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              color: THEME.TEXT_SECONDARY,
              '&:hover': { bgcolor: alpha(THEME.TEXT_SECONDARY, 0.1) }
            }}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success}
            startIcon={loading ? <CircularProgress size={20} /> : <Edit />}
            sx={{
              bgcolor: THEME.ACCENT_WARNING,
              color: '#fff',
              '&:hover': { bgcolor: alpha(THEME.ACCENT_WARNING, 0.8) },
              '&:disabled': { bgcolor: alpha(THEME.ACCENT_WARNING, 0.3) }
            }}
          >
            {loading ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
